// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import Swal from 'sweetalert2';
import { fetchEngineApi } from '@advisor-app/api/engine-api';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
  type ShowModalOptions,
} from '@shared/ui-kit/components/popups/show-modal';
import { useSnackbar, type OptionsObject } from 'notistack';
import { useTranslation } from 'react-i18next';

interface deleteItemPopupProps {
  title: string;
  text: string;
  itemType: string;
  params: any;
  endpoint: string;
}

const commonSnackProps: OptionsObject = {
  variant: 'success',
  anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
};

export const useDeleteItemPopup = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const deleteItemPopup = (
    { title, text, itemType, params, endpoint }: deleteItemPopupProps,
    callBack: (isDeleted: boolean) => void,
  ) => {
    showModal({
      ...MODAL_TEMPLATES_OPTIONS(t).CONFIRM_DELETE,
      title,
      content: text,
      preConfirm: () => {
        return fetchEngineApi({
          endpoint,
          method: 'DELETE',
          params,
        }).catch(() => {
          Swal.showValidationMessage(
            t('errors.request-failed', 'Request failed, please try again', {
              ns: 'errors',
            }),
          );
        });
      },
    } as ShowModalOptions).then(({ isConfirmed }) => {
      if (isConfirmed) {
        callBack(isConfirmed);

        enqueueSnackbar(
          t('common.item-deleted', 'The {{item}} was deleted', {
            ns: 'common',
            item: itemType,
          }),
          {
            ...commonSnackProps,
          },
        );
      }
    });
  };

  return { deleteItemPopup };
};
